import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useBreadcrumbStackOptional, useRegisterBreadcrumbEntry } from "./BreadcrumbStackContext";

export const ARRIVAL_TRAIL_KEY = "crumbTrail";

export const carriedTrail = (state) => {
  const trail = state?.[ARRIVAL_TRAIL_KEY];
  return Array.isArray(trail) && trail.length ? trail : null;
};

export const withArrivalTrail = (state, trail) => (trail?.length ? { ...state, [ARRIVAL_TRAIL_KEY]: trail } : state);

// Trail = ancestors carried in router state by the page the user came from, else `fallbackAncestors`
// (deep link / new tab). The entry keeps its own trail in `state` so Back and crumb clicks restore it.
// `step`: an in-page step on the same route, shown as a crumb after `entry`.
export function useArrivalBreadcrumb(entry, fallbackAncestors, { enabled = true, step = null } = {}) {
  const location = useLocation();
  const ancestors = carriedTrail(location.state) ?? fallbackAncestors;
  const registered = useMemo(
    () => entry && { ...entry, state: withArrivalTrail(entry.state, ancestors) },
    [entry, ancestors]
  );
  const trail = useMemo(() => (step && registered ? [...ancestors, registered] : ancestors), [step, registered, ancestors]);
  useRegisterBreadcrumbEntry(step && registered ? step : registered, { ancestors: trail, exact: true, enabled: enabled && Boolean(entry) });
}

// `child` carries the whole current trail; `sibling` drops the current page so the target sits beside it.
export function useArrivalNavigate(navigate, { as = "child" } = {}) {
  const entries = useBreadcrumbStackOptional()?.entries;
  return useCallback(
    (to, options = {}) => {
      const trail = as === "sibling" ? entries?.slice(0, -1) : entries;
      navigate(to, { ...options, state: withArrivalTrail(options.state, trail) });
    },
    [navigate, entries, as]
  );
}
